import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { authBackendEndPointToken } from '../../app.config';

import { HttpErrorResponse } from '@angular/common/http';
import { throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

 
export interface GlobalRemoteDoctorDto {
  userId: string;
  fhirResourceId: string;
  fullName: string;
  email?: string;
  phoneNumber?: string;
  specialty?: string;
  activeHospitalCount: number;
  hasPendingLinkWithCaller: boolean;
  hasActiveLinkWithCaller: boolean;
}

export interface RemoteDoctorLinkDto {
  id: string;
  remoteDoctorUserId: string;
  remoteDoctorFhirId: string;
  hospitalFhirId: string;
  hospitalOrganizationId: string;
  status: 'Pending' | 'Requested' | 'Active' | 'Suspended' | 'Rejected';
  initiatedBy?: string;
  createdAt: string;
  respondedAt?: string;
  doctorSpecialty?: string;
  notes?: string;
  rejectionReason?: string;
  doctorName?: string;
  hospitalName?: string;
}

export interface GlobalRemoteDoctorRow {
  approvalId?: string;
  userId?: string;
  fhirResourceId?: string;
  fullName: string;
  email?: string;
  phoneNumber?: string;
  specialty?: string;
  qualification?: string;
  licenseNumber?: string;
  submittedAt?: string;
  createdAt?: string;
  status: string;
  requestedHospitals?: string[];
  activeHospitalCount?: number;
  pendingHospitalCount?: number;
  suspendedHospitalCount?: number;
}

export interface HospitalOption {
  id: string;
  name: string;
  type?: string;
  phone?: string;
  email?: string;
  address?: string;
  active?: boolean;
}


export interface ActiveHospitalOption {
  hospitalFhirId: string;
  hospitalName: string;
  isCurrent: boolean;
}
 
export interface MyHospitalsResponse {
  activeHospitalFhirId: string | null;
  hospitals: ActiveHospitalOption[];
}
 
export interface SwitchHospitalResponse {
  message: string;
  activeHospitalFhirId: string;
  activeHospitalName: string | null;
  accessToken: string;
  refreshToken: string;
}

@Injectable({ providedIn: 'root' })
export class RemoteDoctorService {
  private http = inject(HttpClient);
  private base = inject(authBackendEndPointToken);

  // ── Registration ─────────────────────────────────────────────────

  registerRemoteDoctor(payload: any): Observable<any> {
    return this.http.post(`${this.base}/api/Auth/register`, payload);
  }

  getPublicHospitals(): Observable<{ success: boolean; count: number; data: HospitalOption[] }> {
    return this.http.get<any>(`${this.base}/api/Auth/Organization?active=true&_sort=name`);
  }

  // ── Admin: pending / approved / archived global remote doctors ────
  // Matches AdminController.GetGlobalRemoteDoctors (api/[controller] -> "Admin")

  getGlobalRemoteDoctorsByStatus(status: 'pending' | 'active' | 'archived'): Observable<GlobalRemoteDoctorRow[]> {
    return this.http.get<GlobalRemoteDoctorRow[]>(
      `${this.base}/api/admin/global-remote-doctors`,
      { params: { status } }
    );
  }

  approveGlobalDoctor(approvalId: string, specialty: string) {
    return this.http.post(`${this.base}/api/admin/approvals/${approvalId}/approve`, {
      roleConfigurations: [{
        roleName: 'remotedoctor',
        specialty: specialty || null,
        hasFullAccess: true,
        permissions: {
          allowedServiceRequestCategories: [],
          allowedObservationCategories: [],
          customPermissions: {}
        }
      }]
    });
  }

  rejectGlobalDoctor(approvalId: string, reason: string) {
    return this.http.post(`${this.base}/api/admin/approvals/${approvalId}/reject`, { reason });
  }

  archivePractitioner(fhirId: string, reason: string) {
    return this.http.post(`${this.base}/api/admin/practitioners/${fhirId}/archive`, { reason });
  }

  reactivatePractitioner(fhirId: string) {
    return this.http.post(`${this.base}/api/admin/practitioners/${fhirId}/reactivate`, {});
  }


getHospitalLinks(status?: string): Observable<RemoteDoctorLinkDto[]> {
  let params = new HttpParams();
  if (status) params = params.set('status', status);
  return this.http.get<RemoteDoctorLinkDto[]>(`${this.base}/api/remote-doctors/mine`, { params });
}
  // ── Doctor endpoints ─────────────────────────────────────────────

  getMyLinks(status?: string): Observable<RemoteDoctorLinkDto[]> {
    let params = new HttpParams();
    if (status) params = params.set('status', status);
    return this.http.get<RemoteDoctorLinkDto[]>(`${this.base}/api/remote-doctors/mine`, { params });
  }

  applyToHospital(hospitalFhirId: string, notes?: string): Observable<any> {
    return this.http.post(`${this.base}/api/remote-doctors/apply`, { hospitalFhirId, notes });
  }

  respondToLink(linkId: string, accept: boolean, reason?: string): Observable<any> {
    return this.http.post(`${this.base}/api/remote-doctors/links/${linkId}/respond`, { accept, reason });
  }

   /**
   * Active hospitals the current global remote doctor can switch into.
   * Always resolves to a well-formed object, even on partial data —
   * callers don't need to null-check `.hospitals`.
   */
  getMyActiveHospitals(): Observable<MyHospitalsResponse> {
    return this.http.get<MyHospitalsResponse>(`${this.base}/api/remote-doctors/my-hospitals`).pipe(
      map((res) => ({
        activeHospitalFhirId: res?.activeHospitalFhirId ?? null,
        hospitals: Array.isArray(res?.hospitals) ? res.hospitals : [],
      })),
      catchError((err: HttpErrorResponse) => {
        console.error('getMyActiveHospitals failed', err);
        return throwError(() => err);
      })
    );
  }
 
  /**
   * Switch the doctor's active hospital and receive a token scoped to it.
   * Rejects locally (no HTTP call) if hospitalFhirId is missing, so the
   * UI never sends a malformed request.
   */
  switchHospital(hospitalFhirId: string): Observable<SwitchHospitalResponse> {
    if (!hospitalFhirId || !hospitalFhirId.trim()) {
      return throwError(() => new Error('hospitalFhirId is required.'));
    }
 
    return this.http
      .post<SwitchHospitalResponse>(`${this.base}/api/remote-doctors/switch`, { hospitalFhirId })
      .pipe(
        catchError((err: HttpErrorResponse) => {
          console.error('switchHospital failed', err);
          return throwError(() => err);
        })
      );
  }


  // ── Hospital admin endpoints ──────────────────────────────────────
  // Matches RemoteDoctorController.GetGlobalRemoteDoctors (route: api/remote-doctors)

  getGlobalRemoteDoctors(q?: string, page = 1, pageSize = 20)
    : Observable<{ totalCount: number; page: number; pageSize: number; data: GlobalRemoteDoctorDto[] }> {
    let params = new HttpParams()
      .set('page', page)
      .set('pageSize', pageSize);
    if (q) params = params.set('q', q);
    return this.http.get<any>(`${this.base}/api/remote-doctors/global`, { params });
  }

  inviteRemoteDoctor(remoteDoctorFhirId: string, notes?: string): Observable<any> {
    return this.http.post(
      `${this.base}/api/remote-doctors/invite/${remoteDoctorFhirId}`,
      { remoteDoctorFhirId, notes }
    );
  }

  suspendLink(linkId: string, reason?: string): Observable<any> {
    return this.http.post(`${this.base}/api/remote-doctors/links/${linkId}/suspend`, { accept: false, reason });
  }
}